import {getEnv} from "../cms/env";

declare global {
    interface Window {
        RUNTIME_CONFIG?: {
            YANDEX_METRIKA_ID?: string;
        };
        ym?: YandexMetrikaFn;
    }
}

type YandexMetrikaFn = {
    (...args: any[]): void;
    a?: any[][];
    l?: number;
};

type ReachGoalParams = Record<string, string | number | boolean>;

const scriptId = "yandex-metrika-tag";
const isDev = process.env.NODE_ENV === "development";

let initialized = false;
let lastTrackedUrl: string | null = null;

function debugLog(...args: unknown[]) {
    if (isDev) {
        console.log("[metrika]", ...args);
    }
}

// Берёт runtime-конфиг из window, а в локальной разработке умеет падать обратно в build-time env.
function getCounterId() {
    if (typeof window !== "undefined") {
        const runtimeCounterId = window.RUNTIME_CONFIG?.YANDEX_METRIKA_ID?.trim();
        if (runtimeCounterId) return runtimeCounterId;
    }

    return getEnv("REACT_APP_YANDEX_METRIKA_ID");
}

// Проверяет, что код выполняется в браузере, а не в тестовом/серверном окружении.
function isBrowser() {
    return typeof window !== "undefined" && typeof document !== "undefined";
}

// Создаёт временную очередь вызовов ym до полной загрузки скрипта Метрики.
function ensureYmStub() {
    if (!isBrowser() || window.ym) return;

    const ym = function (...args: any[]) {
        (ym.a = ym.a || []).push(args);
    } as YandexMetrikaFn;

    ym.l = Date.now();
    window.ym = ym;
}

// Подключает внешний скрипт Метрики один раз за всё время жизни приложения.
function injectTagScript() {
    if (!isBrowser()) return;
    if (document.getElementById(scriptId)) return;

    const script = document.createElement("script");
    script.id = scriptId;
    script.async = true;
    script.src = "https://mc.yandex.ru/metrika/tag.js";
    document.head.appendChild(script);
}

// Возвращает true, если в окружении задан ID счётчика и Метрика может быть включена.
export function isMetrikaEnabled() {
    return Boolean(getCounterId());
}

// Инициализирует счётчик в режиме SPA: без авто-pageview и без повторной инициализации.
export function initMetrika() {
    const counterId = getCounterId();
    if (!isBrowser() || !counterId || initialized) return;

    ensureYmStub();
    injectTagScript();
    window.ym?.(counterId, "init", {
        defer: true,
        clickmap: true,
        trackLinks: true,
        accurateTrackBounce: true,
        webvisor: true,
    });
    initialized = true;
    debugLog("init", counterId);
}

// Отправляет pageview вручную при смене маршрута и отсекает дубли одного URL.
export function trackPageView(url?: string) {
    const counterId = getCounterId();
    if (!counterId || !isBrowser()) return;

    initMetrika();

    const nextUrl = url || `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (lastTrackedUrl === nextUrl) return;

    window.ym?.(counterId, "hit", nextUrl, {
        title: document.title,
        referer: document.referrer || undefined,
    });

    lastTrackedUrl = nextUrl;
    debugLog("hit", nextUrl);
}

// Отправляет пользовательскую цель, которую можно использовать в отчётах и конверсиях.
export function reachGoal(goal: string, params?: ReachGoalParams) {
    const counterId = getCounterId();
    if (!counterId || !isBrowser()) return;

    initMetrika();
    window.ym?.(counterId, "reachGoal", goal, params);
    debugLog("reachGoal", goal, params);
}
